/*
 * ADMIN LAYOUT — Brutalist sidebar navigation for admin pages
 * Uses PlayXworld monochrome brutalism style (not DashboardLayout)
 */
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { Link, useLocation } from "wouter";
import {
  LayoutDashboard,
  ShoppingCart,
  Users,
  Wallet,
  Gamepad2,
  ArrowLeft,
  Shield,
  Loader2,
  Menu,
  X,
  RefreshCw,
} from "lucide-react";
import { useState } from "react";

const ADMIN_MENU = [
  { icon: LayoutDashboard, label: "Dashboard", path: "/admin" },
  { icon: Gamepad2, label: "Games", path: "/admin/games" },
  { icon: RefreshCw, label: "Game Sync", path: "/admin/game-sync" },
  { icon: ShoppingCart, label: "Transactions", path: "/admin/orders" },
  { icon: Users, label: "Users", path: "/admin/users" },
  { icon: Wallet, label: "Audit Logs", path: "/admin/audit-log" },
];

export default function AdminLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { data: firebaseUser, isLoading: userLoading } =
    trpc.auth.me.useQuery(undefined, { enabled: isAuthenticated });
  const [location] = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isLoading = authLoading || (isAuthenticated && userLoading);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 size={32} className="animate-spin text-black/40" />
          <span className="font-mono text-xs text-black/40 tracking-widest">
            LOADING...
          </span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || !firebaseUser) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="border-3 border-black p-8 max-w-md text-center">
          <Shield size={48} className="mx-auto text-black/20 mb-4" />
          <h1 className="font-heading font-bold text-2xl tracking-tight mb-2">
            กรุณาเข้าสู่ระบบ
          </h1>
          <p className="font-mono text-xs text-black/50 mb-6">
            ต้องเข้าสู่ระบบเพื่อเข้าถึงหน้า Admin
          </p>
          <Link
            href="/"
            className="btn-brutal bg-black text-white inline-flex items-center gap-2 text-sm"
          >
            <ArrowLeft size={14} />
            กลับหน้าแรก
          </Link>
        </div>
      </div>
    );
  }

  if (firebaseUser.role !== "admin") {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="border-3 border-black p-8 max-w-md text-center">
          <Shield size={48} className="mx-auto text-red-500/50 mb-4" />
          <h1 className="font-heading font-bold text-2xl tracking-tight mb-2">
            ไม่มีสิทธิ์เข้าถึง
          </h1>
          <p className="font-mono text-xs text-black/50 mb-6">
            คุณไม่มีสิทธิ์ Admin กรุณาติดต่อผู้ดูแลระบบ
          </p>
          <Link
            href="/"
            className="btn-brutal bg-black text-white inline-flex items-center gap-2 text-sm"
          >
            <ArrowLeft size={14} />
            กลับหน้าแรก
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-black flex">
      {/* Sidebar — Desktop */}
      <aside className="hidden md:flex flex-col w-60 border-r-3 border-black bg-white shrink-0 sticky top-0 h-screen">
        {/* Logo */}
        <div className="p-4 border-b-3 border-black">
          <Link href="/" className="flex items-center gap-2 group">
            <div className="w-8 h-8 bg-black text-white flex items-center justify-center font-heading font-bold text-xs">
              PX
            </div>
            <span className="font-heading font-bold text-sm tracking-tight">
              ADMIN
            </span>
          </Link>
        </div>

        {/* Menu */}
        <nav className="flex-1 py-2">
          {ADMIN_MENU.map((item) => {
            const isActive = location === item.path;
            return (
              <Link
                key={item.path}
                href={item.path}
                className={`flex items-center gap-3 px-4 py-3 font-mono text-xs tracking-wider transition-colors ${
                  isActive
                    ? "bg-black text-white"
                    : "text-black/60 hover:bg-black/5 hover:text-black"
                }`}
              >
                <item.icon size={16} />
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t-3 border-black">
          <div className="flex items-center gap-2 mb-3">
            {firebaseUser.profileImage ? (
              <img
                src={firebaseUser.profileImage}
                alt=""
                className="w-8 h-8 border-2 border-black object-cover"
              />
            ) : (
              <div className="w-8 h-8 border-2 border-black flex items-center justify-center bg-black/5">
                <Shield size={12} />
              </div>
            )}
            <div className="min-w-0">
              <div className="font-mono text-xs font-semibold truncate">
                {firebaseUser.name || "Admin"}
              </div>
              <div className="font-mono text-[9px] text-black/40 tracking-wider">
                ADMIN
              </div>
            </div>
          </div>
          <Link
            href="/"
            className="flex items-center gap-2 font-mono text-[10px] text-black/40 hover:text-black transition-colors tracking-wider"
          >
            <ArrowLeft size={12} />
            กลับหน้าเว็บ
          </Link>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b-3 border-black">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-black text-white flex items-center justify-center font-heading font-bold text-[10px]">
              PX
            </div>
            <span className="font-heading font-bold text-sm tracking-tight">
              ADMIN
            </span>
          </div>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="border-2 border-black p-1.5"
          >
            {mobileMenuOpen ? <X size={16} /> : <Menu size={16} />}
          </button>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <nav className="border-t-2 border-black bg-white">
            {ADMIN_MENU.map((item) => {
              const isActive = location === item.path;
              return (
                <Link
                  key={item.path}
                  href={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 font-mono text-xs tracking-wider transition-colors ${
                    isActive
                      ? "bg-black text-white"
                      : "text-black/60 hover:bg-black/5"
                  }`}
                >
                  <item.icon size={16} />
                  {item.label}
                </Link>
              );
            })}
            <Link
              href="/"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3 font-mono text-xs tracking-wider text-black/40 hover:text-black border-t border-black/10"
            >
              <ArrowLeft size={16} />
              กลับหน้าเว็บ
            </Link>
          </nav>
        )}
      </div>

      {/* Main Content */}
      <main className="flex-1 min-w-0 md:p-8 p-4 pt-20 md:pt-8">
        {children}
      </main>
    </div>
  );
}
